import React, { FC, memo, useContext } from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';

import styles, { BASE_FONT_SIZE } from '../pdfStyles';
import PdfLogoComponent from '../PdfComponents/PdfLogo';
import PdfFooter from '../PdfComponents/PdfFooter';
import PdfHeaderDetails from '../PdfComponents/PdfHeaderDetails';
import PdfMainDetails from '../PdfComponents/PdfMainDetails';
import PdfPassengerDetails from '../PdfComponents/PdfPassengerDetails';
import PdfTableComponent from '../PdfComponents/PdfTable';
import { PdfRemarksSection } from '../PdfComponents/PdfSection';
import { voucherStatusesEnum } from '../../../api/vouchers/helpers';
import PdfBillingContactDetails from '../PdfComponents/PdfBillingContactDetails';
import { Context as ClientsContext } from '../../../api/clients/context';
import { Client } from '../../../api/clients/types';
import { VouchersPdfPropsType } from './voucherHelpers';

const TaxInvoicePdf: FC<VouchersPdfPropsType> = (props: VouchersPdfPropsType) => {
    const clientsContext = useContext(ClientsContext);

    const {
        info: {
            dueDate,
            travelFileNumber,
            travelFileOpenDate,
            travelFileStartDate,
            remark,
            pdfInfo: {
                companyInfo,
                userName,
                showIssuedBy,
                pdfImageSource = (clientsContext.selected as Client)?.companyLogoUrl,
                passenger,
                agentInfo,
                supplierInfo,
                columns,
                rows,
                summaryLine,
                rateTableColumns,
                rateTableRows,
                rateLine,
                equivalentAmountText,
                bottomDetails,
                topLeftDetailsText,
                allocationNumber,
                voucherCertification = 'Draft'
            }
        },
        number: voucherNumber,
        type: voucherType,
        status,
        createdAt
    } = props;

    return (
        <Document title={`${voucherType} ${voucherNumber || ''}`}>
            <Page wrap size="A4" style={styles.page}>
                <PdfLogoComponent pdfImageSource={pdfImageSource} />

                <View style={styles.padding0_10}>
                    <View style={styles.section}>
                        <View style={{ ...styles.header, ...styles.spaceBetween }}>
                            <View>
                                {topLeftDetailsText ? <Text>{topLeftDetailsText}</Text> : <></>}
                                {passenger ? <PdfPassengerDetails passenger={passenger} withoutTo /> : <></>}
                                {agentInfo ? <PdfBillingContactDetails {...agentInfo} /> : <></>}
                                {supplierInfo ? <PdfBillingContactDetails {...supplierInfo} /> : <></>}
                            </View>

                            <PdfHeaderDetails
                                companyVatNumber={companyInfo.vat}
                                iata={companyInfo.iata}
                                issueDate={props.issueDate || createdAt}
                                dueDate={dueDate}
                                travelFileNumber={travelFileNumber}
                                travelFileOpenDate={travelFileOpenDate}
                                travelFileStartDate={travelFileStartDate}
                            />
                        </View>
                    </View>
                    <View style={[styles.alignItemsFlexEnd, styles.margin2_10]}>
                        <Text style={{ ...styles.button }}>{voucherCertification}</Text>
                    </View>

                    <View style={[styles.section, styles.alignCenter]} wrap={false}>
                        {status === voucherStatusesEnum.CANCEL ? (
                            <Text style={[styles.paymentStatus, styles.marginTopMinus24BottomMinus14]}>
                                {'CANCELLED'}
                            </Text>
                        ) : (
                            <></>
                        )}
                        <PdfMainDetails
                            topDetails={`${voucherType} No. ${voucherNumber || 'No Document Number'}`}
                            bottomDetails={bottomDetails}
                            withForLabel
                        />
                        {allocationNumber ? (
                            <Text
                                style={[
                                    styles.fontBold,
                                    {
                                        fontSize: BASE_FONT_SIZE + 2,
                                        marginTop: 4,
                                        textAlign: 'center',
                                        color: '#007566'
                                    }
                                ]}
                            >
                                {`מספר הקצאה: ${allocationNumber}`}
                            </Text>
                        ) : (
                            <></>
                        )}
                    </View>

                    <View style={[styles.section, styles.marginBottom10px]}>
                        <PdfTableComponent columns={columns} rows={rows} summaryLine={summaryLine} />
                    </View>

                    {rateLine || equivalentAmountText ? (
                        <View style={[styles.section, styles.marginBottom10px]}>
                            {rateLine ? <Text style={[styles.littleText, styles.fontBold]}>{rateLine}</Text> : <></>}
                            {equivalentAmountText ? <Text style={[styles.littleText, styles.fontBold]}>{equivalentAmountText}</Text> : <></>}
                        </View>
                    ) : rateTableRows?.length ? (
                        <View style={[styles.section, styles.marginBottom10px]}>
                            <PdfTableComponent
                                columns={rateTableColumns}
                                rows={rateTableRows}
                                tableStyle={styles.rateTableWidth}
                            />
                        </View>
                    ) : (
                        <></>
                    )}

                    <PdfRemarksSection body={remark} />

                    {showIssuedBy !== false && !!userName && <Text style={{ fontSize: BASE_FONT_SIZE - 1, color: '#6b7280', marginTop: 6, marginHorizontal: 10 }}>Issued by: {userName}</Text>}
                </View>

                <PdfFooter companyInfo={companyInfo} />
            </Page>
        </Document>
    );
};

export default memo(TaxInvoicePdf);
